<template>
  <div class="home">
    <LoadingIndicator :isVisible="isLoading" :type="LOADING_INDICATOR_TYPES.cloudCentered" />
    <div v-if="!isLoading" class="home__container">
      <div
        class="home__block"
        :class="`home__block--index-${blockIndex}`"
        v-for="(block, blockIndex) in blocks"
        :key="blockIndex"
        v-data-test-id="'home-block'"
      >
        <Typography
          v-if="block.title"
          class="home__block-title"
          :variant="TYPOGRAPHY_VARIANTS.H2"
          :type="TYPOGRAPHY_TYPES.HEADING_MEDIUM"
          :isBold="true"
          >{{ block.title }}</Typography
        >
        <div class="home__block-components">
          <IntersectionObserver
            v-for="(component, componentIndex) in block.components"
            :key="componentIndex"
            :isEnabled="!!component.intersectionObserverData"
            @on-intersection="() => onComponentIntersection(component.intersectionObserverData)"
          >
            <component
              :is="component.type"
              v-bind="component.props"
              class="home__block-component"
              :class="`home__block-component--${component.type}`"
              @on-intersection="onComponentIntersection"
            />
          </IntersectionObserver>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Hero,
  Secondary,
  Typography,
  TYPOGRAPHY_VARIANTS,
  TYPOGRAPHY_TYPES,
  BUTTON_VARIANTS,
} from 'on-ui'
import { introAnimation } from '@/helpers/animation'
import HomepageService from '@/api/services/homepage-service'
import { isEmpty } from '@/helpers/array'
import LoadingIndicator, {
  LOADING_INDICATOR_TYPES,
} from '@/components/_shared/LoadingIndicator.vue'
import IntersectionObserver from '@/components/_shared/IntersectionObserver.vue'
import CategorySlider from '@/components/Home/CategorySlider/CategorySlider.vue'
import Recommendations from '@/components/Home/Recommendations.vue'
import templates from '@/components/Home/templates.json'

const toTrackingId = (position, index) => `${position}-${index + 1}`

export default {
  name: 'Home',
  components: {
    LoadingIndicator,
    IntersectionObserver,
    Typography,
    Recommendations,
    Hero,
    Secondary,
    CategorySlider,
  },

  data() {
    return {
      isLoading: false,
      result: {},
      blocks: [],
      LOADING_INDICATOR_TYPES,
      TYPOGRAPHY_VARIANTS,
      TYPOGRAPHY_TYPES,
    }
  },

  created() {
    this.fetchData()
  },

  methods: {
    async fetchData() {
      this.isLoading = true
      this.result = await HomepageService.fetch(this)
      this.populateTemplateBlocks(this.result.template)
      this.isLoading = false
      this.reportPageVersion()
    },

    async populateTemplateBlocks(templateName) {
      const template = templates[templateName]
      this.blocks = this.addData(template)
      await this.$nextTick()
      introAnimation([
        '.category-slider__slide',
        '.product-recommendations__title',
        '.product-recommendations__products .card',
        '.home__block-title',
        '.home__block-component:not(.home__block-component--Hero)',
      ])
    },

    addData(template) {
      const currentDataEntryIndices = {}
      return template.blocks.reduce((blocks, block, index) => {
        const title = this.$t(block.title)
        const components = this.getComponents(block, currentDataEntryIndices)
        if (!isEmpty(components)) {
          blocks.push({ title, components, index })
        }
        return blocks
      }, [])
    },

    getComponents(block, currentDataEntryIndices) {
      return block.components.reduce((components, component) => {
        const { type } = component
        const index = currentDataEntryIndices[type] || 0
        const props = this.getComponentProps(type, index)
        const intersectionObserverData = this.getIntersectionObserverData(type, index)
        currentDataEntryIndices[type] = index + 1
        if (props && this.isComponentRegistered(type)) {
          components.push({ type, props, intersectionObserverData })
        }
        return components
      }, [])
    },

    getIntersectionObserverData(type, index) {
      const TYPE_TO_RESULT_KEY_MAP = {
        Hero: 'promoPrimary',
        Secondary: 'promoSecondary',
      }
      const key = TYPE_TO_RESULT_KEY_MAP[type]
      if (!key) {
        return null
      }
      const entry = this.result[key]?.[index]
      return {
        title: entry?.title,
        imageUrl: entry?.backgroundImage?.desktop || entry?.imageFileName,
        type: key,
        index,
      }
    },

    onComponentIntersection(data) {
      if (!data) return
      const { title, imageUrl, type, index } = data
      this.trackView({ text: title, imageUrl, positionName: type, index })
    },

    getComponentProps(type, index) {
      const BLOCK_COMPONENT_PROPS_ACCESSORS = {
        // return null/undefined, if you don't want to render the current component
        Hero: (result, index) => this.getPropsForPromoPrimary(result.promoPrimary, index),
        Secondary: (result, index) => this.getPropsForPromoSecondary(result.promoSecondary, index),
        CategorySlider: result => this.getPropsForCategorySlider(result.categorySlider),
        Recommendations: () => ({}),
      }
      return BLOCK_COMPONENT_PROPS_ACCESSORS[type]?.(this.result, index)
    },

    getPropsForPromoPrimary(data, index) {
      if (!data?.[index]?.title) return
      const entry = data[index]
      const {
        title,
        campaignIds,
        imageFileName,
        mobileImageFileName,
        headline,
        videoJpegUrl,
        videoMp4Url,
        videoOgvUrl,
        videoWebmUrl,
      } = entry
      const ctas = this.makeHomepageCtas(entry)

      return {
        ...entry,
        superTitle: headline,
        backgroundUrlDesktop: imageFileName,
        backgroundUrlMobile: mobileImageFileName,
        videoUrl: videoJpegUrl || videoMp4Url || videoOgvUrl || videoWebmUrl,
        cta: ctas.map(({ content, url }) => ({
          content,
          url,
          variant: BUTTON_VARIANTS.ANCHOR,
          onClick: () => {
            this.trackClick({
              text: title,
              imageUrl: imageFileName,
              positionName: 'hero',
              index,
              campaignIds,
            })
          },
        })),
      }
    },

    getPropsForPromoSecondary(data, index) {
      if (!data?.[index]?.title) return
      const entry = data[index]
      const { title, campaignIds, imageFileName, headline } = entry
      const ctas = this.makeHomepageCtas(entry)
      return {
        ...entry,
        superTitle: headline,
        backgroundUrlDesktop: imageFileName,
        backgroundUrlMobile: imageFileName,
        cta: ctas.map(({ content, url }) => ({
          content,
          url,
          variant: BUTTON_VARIANTS.ANCHOR,
          onClick: () => {
            this.trackClick({
              text: title,
              imageUrl: imageFileName,
              positionName: 'secondary',
              index,
              campaignIds,
            })
          },
        })),
      }
    },

    makeHomepageCtas({ button1 = '', buttonLink1 = '', button2 = '', buttonLink2 = '' }) {
      return [
        {
          content: button1,
          url: buttonLink1,
        },
        {
          content: button2,
          url: buttonLink2,
        },
      ]
    },

    getPropsForHeroBanner(data, index) {
      return this.getPropsForBanner(data, index, 'hero')
    },

    getPropsForSecondaryBanner(data, index) {
      return this.getPropsForBanner(data, index, 'secondary')
    },

    getPropsForBanner(data, index, positionName) {
      if (!data?.[index]?.title) return
      // get current Banner data (Hero or Secondary)
      const { backgroundImage, campaignIds, ctas = [], title, superTitle, videoUrl } = data[index]
      const imageUrlDesktop = backgroundImage?.desktop
      return {
        title,
        superTitle,
        videoUrl,
        backgroundUrlDesktop: imageUrlDesktop,
        backgroundUrlMobile: backgroundImage?.mobile,
        cta: ctas.map(({ text, url }) => ({
          content: text,
          url,
          variant: BUTTON_VARIANTS.ANCHOR,
          onClick: () => {
            this.trackClick({
              text: title,
              imageUrl: imageUrlDesktop,
              positionName,
              index,
              campaignIds,
            })
          },
        })),
      }
    },

    getPropsForCategorySlider(data) {
      if (!data || isEmpty(data)) return
      // all CategorySliders will have the same data
      return {
        categories: data.reduce((categories, entry, index) => {
          const { title, ctas, backgroundImage, campaignIds } = entry
          if (title && ctas?.[0]?.text && ctas?.[0]?.url) {
            const imageUrlDesktop = backgroundImage?.desktop
            categories.push({
              title,
              ctaCopy: ctas[0].text,
              ctaUrl: ctas[0].url,
              backgroundUrl: imageUrlDesktop,
              onClick: () => {
                this.trackClick({
                  text: title,
                  imageUrl: imageUrlDesktop,
                  positionName: 'category',
                  index,
                  campaignIds,
                })
              },
            })
          }
          return categories
        }, []),
      }
    },

    reportPageVersion() {
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.homepageVersion,
        homepageVersion: this.result.template,
      })
    },

    toTrackingIdMapper(positionName) {
      const homepagePromoMapping = {
        promoPrimary: 'hero',
        promoSecondary: 'secondary',
      }

      return homepagePromoMapping[positionName] || positionName
    },

    trackClick({ text, imageUrl, positionName, index, campaignIds }) {
      const mappedPositionName = this.toTrackingIdMapper(positionName)
      const identifier = toTrackingId(mappedPositionName, index)
      this.track({ text, imageUrl, identifier, action: this.$notifier.ACTIONS.promotionClick })
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.campaignClick,
        campaignIds,
        data: { identifier },
      })
    },

    trackView({ text, imageUrl, positionName, index }) {
      const mappedPositionName = this.toTrackingIdMapper(positionName)
      const identifier = toTrackingId(mappedPositionName, index)
      this.track({ text, imageUrl, identifier, action: this.$notifier.ACTIONS.promotionView })
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.campaignView,
        data: { identifier },
      })
    },

    track({ text, imageUrl, identifier, action }) {
      this.$notifier.trackEvent({
        action,
        promotions: [
          {
            name: text,
            creative: imageUrl,
            position: identifier,
          },
        ],
      })
    },

    isComponentRegistered(type) {
      return !!this.$options.components[type]
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/stylesheets/base/animations.scss';

$gutter-small: 24px;
$gutter-large: 48px;
$max-content-width: 2200px;
$hero-mobile-max-height: 162vw; // maintains mobile apect ratio for mobile background
$hero-desktop-max-height: 56.25vw; // 16:9 aspect ratio

.home {
  max-width: $max-content-width;
  margin: 0 auto 100px;

  &__block--index-1 {
    .intro-animation--visible {
      transition-delay: 1000ms;
    }
  }

  &__block-title {
    margin: 28px 0 -4px;
    text-align: center;

    @media ($mobile-up) {
      margin-top: $gutter-large;
    }

    @media ($tablet-large-up) {
      margin-top: 88px;
    }
  }

  &__block-components {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    @media ($tablet-up) {
      margin: 0 $gutter-small;
    }

    @media ($tablet-large-up) {
      margin: 0 $gutter-large;
    }
  }

  &__block-component {
    flex-grow: 1;
    flex-basis: calc(100% - (2 * #{$gutter-small}));
    margin: $gutter-small $gutter-small 0;

    @media ($tablet-up) {
      margin: $gutter-small $gutter-small 0 0;

      // prevents extra margin at the end of last block component
      &:last-child:not(.home__block-component--Hero):not(.home__block-component--Recommendations) {
        margin-right: 0;
      }

      // prevent extra margin at every second block component
      &:nth-child(2n) {
        margin-right: 0;
      }
    }

    @media ($tablet-large-up) {
      flex-basis: calc(100% - (2 * #{$gutter-large}));
      margin: $gutter-large $gutter-large 0 0;
    }

    &--Hero {
      height: calc(100vh - #{$header-height});
      max-height: $hero-mobile-max-height;
      margin: 0;

      /* FIX ME later */
      // /* mobile portrait viewport bug fix to match full height beyond menu bars */
      // @include mq-ios-small-portrait {
      //   height: calc(115vh - (2 * #{$gutter-small} + #{$header-height}));
      // }

      @media ($tablet-up) {
        margin: 0 -#{$gutter-small};
      }

      @media ($tablet-large-up) {
        height: calc(#{$hero-desktop-max-height} - 70px);
        margin: 0 -#{$gutter-large};
        max-height: $hero-desktop-max-height; // 16:9 aspect ratio
      }

      @media (min-width: #{$max-content-width}) {
        margin: 0 calc((#{$max-content-width} - 100vw) / 2 - #{$gutter-large});
      }
    }

    &--Secondary {
      height: 450px;

      @media ($tablet-up) {
        flex-basis: calc(50% - (2 * #{$gutter-small}));
      }

      @media ($tablet-large-up) {
        height: 556px;
        flex-basis: calc(50% - (2 * #{$gutter-large}));
      }

      @media ($desktop-huge-up) {
        height: 752px;
      }
    }

    &--CategorySlider {
      width: calc(100% - #{$gutter-small * 2});

      @media ($tablet-large-up) {
        width: calc(100% - #{$gutter-large * 2});
      }
    }

    &--Recommendations {
      @media ($tablet-up) {
        margin-right: 0;
        width: 100%;
      }

      /deep/ .cards {
        flex-flow: row nowrap;
        align-items: stretch;
        justify-content: flex-start !important;

        .card {
          flex: 1 1 260px;
          margin: 0 12px 0 0 !important;

          &:last-child {
            margin: 0 !important;
          }
        }

        @media ($tablet-large-up) {
          .card {
            margin: 0 24px 0 0 !important;
          }
        }
      }
    }
  }
}
</style>

<i18n src="@/locales/home.json">
</i18n>
